import { useState, useEffect, useRef } from 'react';
import api from '../../api';
import ChannelSelect from '../../components/ui/ChannelSelect';

const STATUS_CONFIG = {
  pending: { color: 'var(--warning)', label: 'Pending', emoji: '⏳' },
  approved: { color: 'var(--success)', label: 'Approved', emoji: '✅' },
  denied: { color: 'var(--danger)', label: 'Denied', emoji: '❌' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px', background: cfg.color + '20', color: cfg.color }}>
      {cfg.emoji} {cfg.label}
    </span>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 12px', boxSizing: 'border-box',
  border: '2px solid var(--border)', borderRadius: '10px',
  fontSize: '14px', fontFamily: 'var(--font-body)',
  background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
};

const cardStyle = {
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: '16px', padding: '20px 24px', boxShadow: 'var(--shadow-card)',
};

function QuestionForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    question_text: '', question_type: 'text', required: true,
    min_length: '', max_length: '', options: ['', ''],
  });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div>
        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Question</label>
        <input type="text" value={form.question_text} onChange={e => update('question_text', e.target.value)} placeholder="e.g. Why do you want to join?" style={inputStyle} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Response Type</label>
          <select value={form.question_type} onChange={e => update('question_type', e.target.value)} style={inputStyle}>
            <option value="text">Text Response</option>
            <option value="multiple_choice">Multiple Choice</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Required</label>
          <select value={form.required ? 'yes' : 'no'} onChange={e => update('required', e.target.value === 'yes')} style={inputStyle}>
            <option value="yes">Yes — required</option>
            <option value="no">No — optional</option>
          </select>
        </div>
      </div>
      {form.question_type === 'text' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Min Length (optional)</label>
            <input type="number" min="0" value={form.min_length} onChange={e => update('min_length', e.target.value)} placeholder="e.g. 50" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Max Length (optional)</label>
            <input type="number" min="0" value={form.max_length} onChange={e => update('max_length', e.target.value)} placeholder="e.g. 500" style={inputStyle} />
          </div>
        </div>
      )}
      {form.question_type === 'multiple_choice' && (
        <div>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Options</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {form.options.map((opt, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px' }}>
                <input type="text" value={opt} onChange={e => setForm(p => ({ ...p, options: p.options.map((o, idx) => idx === i ? e.target.value : o) }))}
                  placeholder={`Option ${i + 1}`} style={{ ...inputStyle, flex: 1 }} />
                {form.options.length > 2 && (
                  <button onClick={() => setForm(p => ({ ...p, options: p.options.filter((_, idx) => idx !== i) }))}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--danger)', background: 'transparent', color: 'var(--danger)', cursor: 'pointer' }}>×</button>
                )}
              </div>
            ))}
            {form.options.length < 10 && (
              <button onClick={() => setForm(p => ({ ...p, options: [...p.options, ''] }))}
                style={{ padding: '8px', borderRadius: '8px', border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>+ Add option</button>
            )}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={() => onSave(form)} style={{ flex: 1, padding: '12px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>Save Question</button>
        <button onClick={onCancel} style={{ padding: '12px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>Cancel</button>
      </div>
    </div>
  );
}

export default function ApplicationsPage({ guild }) {
  const [tab, setTab] = useState('submissions');
  const [forms, setForms] = useState([]);
  const [panels, setPanels] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Forms tab state
  const [selectedForm, setSelectedForm] = useState(null);
  const [formQuestions, setFormQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [creatingQuestion, setCreatingQuestion] = useState(false);
  const [creatingForm, setCreatingForm] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [newFormName, setNewFormName] = useState('');
  const [newFormDesc, setNewFormDesc] = useState('');
  const [newFormTimeout, setNewFormTimeout] = useState(15);

  // Panels tab state
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [creatingPanel, setCreatingPanel] = useState(false);
  const [editingPanel, setEditingPanel] = useState(null);
  const [editPanelForms, setEditPanelForms] = useState([]);
  const [newPanelName, setNewPanelName] = useState('');
  const [newPanelDesc, setNewPanelDesc] = useState('');
  const [newPanelForms, setNewPanelForms] = useState([]);
  const [pushingPanel, setPushingPanel] = useState(null);
  const [pushChannel, setPushChannel] = useState('');

  // Submissions tab state
  const [subFilter, setSubFilter] = useState('pending');
  const [subFormFilter, setSubFormFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [actioning, setActioning] = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [denyModal, setDenyModal] = useState(null);
  const [denyNote, setDenyNote] = useState('');
  const [approveNote, setApproveNote] = useState('');
  const [addToStaff, setAddToStaff] = useState(false);
  const [approveRank, setApproveRank] = useState('');

  const pendingCount = submissions.filter(s => s.status === 'pending').length;

  const loadAll = async () => {
    setLoading(true);
    try {
      const [fRes, pRes, sRes, rRes] = await Promise.all([
        api.get(`/api/applications/${guild.id}/forms`),
        api.get(`/api/applications/${guild.id}/panels`),
        api.get(`/api/applications/${guild.id}?status=all`),
        api.get(`/api/guilds/${guild.id}/ranks`),
      ]);
      setForms(fRes.data);
      setPanels(pRes.data);
      setSubmissions(sRes.data);
      setRanks(rRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadQuestions = async (formId) => {
    const res = await api.get(`/api/applications/${guild.id}/forms/${formId}/questions`);
    setFormQuestions(res.data);
  };

  useEffect(() => { loadAll(); }, [guild.id]);
  useEffect(() => { if (selectedForm) loadQuestions(selectedForm.id); }, [selectedForm]);

  // ── Forms ─────────────────────────────────────────────────────────────────

  const handleCreateForm = async () => {
    if (!newFormName.trim()) return alert('Please enter a form name.');
    try {
      await api.post(`/api/applications/${guild.id}/forms`, { name: newFormName, description: newFormDesc, timeout_minutes: parseInt(newFormTimeout) || 15 });
      setCreatingForm(false); setNewFormName(''); setNewFormDesc(''); setNewFormTimeout(15);
      await loadAll();
    } catch { alert('Failed to create form.'); }
  };

  const handleToggleFormStatus = async (form) => {
    try {
      await api.patch(`/api/applications/${guild.id}/forms/${form.id}`, { status: form.status === 'open' ? 'closed' : 'open' });
      await loadAll();
    } catch { alert('Failed to update form.'); }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Delete this form? All questions and submissions for this form will be deleted.')) return;
    try {
      await api.delete(`/api/applications/${guild.id}/forms/${formId}`);
      if (selectedForm?.id === formId) setSelectedForm(null);
      await loadAll();
    } catch { alert('Failed to delete form.'); }
  };

  const handleUpdateForm = async () => {
    if (!editingForm) return;
    try {
      await api.patch(`/api/applications/${guild.id}/forms/${editingForm.id}`, {
        name: editingForm.name,
        description: editingForm.description,
        timeout_minutes: parseInt(editingForm.timeout_minutes) || 15,
      });
      setEditingForm(null);
      await loadAll();
    } catch { alert('Failed to update form.'); }
  };

  const handleUpdatePanel = async () => {
    if (!editingPanel) return;
    try {
      await api.patch(`/api/applications/${guild.id}/panels/${editingPanel.id}`, {
        name: editingPanel.name,
        description: editingPanel.description,
        form_ids: editPanelForms,
      });
      setEditingPanel(null);
      setEditPanelForms([]);
      await loadAll();
    } catch { alert('Failed to update panel.'); }
  };

  const handleSaveQuestion = async (form) => {
    const payload = {
      question_text: form.question_text, question_type: form.question_type, required: form.required,
      min_length: form.min_length ? parseInt(form.min_length) : null,
      max_length: form.max_length ? parseInt(form.max_length) : null,
      options: form.question_type === 'multiple_choice' ? form.options.filter(o => o.trim()) : null,
    };
    try {
      if (editingQuestion) {
        await api.patch(`/api/applications/${guild.id}/forms/${selectedForm.id}/questions/${editingQuestion.id}`, payload);
      } else {
        await api.post(`/api/applications/${guild.id}/forms/${selectedForm.id}/questions`, payload);
      }
      setEditingQuestion(null); setCreatingQuestion(false);
      await loadQuestions(selectedForm.id);
    } catch { alert('Failed to save question.'); }
  };

  const handleDeleteQuestion = async (qId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/api/applications/${guild.id}/forms/${selectedForm.id}/questions/${qId}`);
      await loadQuestions(selectedForm.id);
    } catch { alert('Failed to delete question.'); }
  };

  // ── Panels ────────────────────────────────────────────────────────────────

  const handleCreatePanel = async () => {
    if (!newPanelName.trim()) return alert('Please enter a panel name.');
    try {
      await api.post(`/api/applications/${guild.id}/panels`, { name: newPanelName, description: newPanelDesc, form_ids: newPanelForms });
      setCreatingPanel(false); setNewPanelName(''); setNewPanelDesc(''); setNewPanelForms([]);
      await loadAll();
    } catch { alert('Failed to create panel.'); }
  };

  const handlePushPanel = async (panelId) => {
    setPushingPanel(panelId);
    try {
      await api.post(`/api/applications/${guild.id}/panels/${panelId}/push`, { channel_id: pushChannel || undefined });
      alert('Panel pushed to Discord!');
      setPushChannel('');
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to push panel.');
    } finally { setPushingPanel(null); }
  };

  const handleDeletePanel = async (panelId) => {
    if (!window.confirm('Delete this panel?')) return;
    try {
      await api.delete(`/api/applications/${guild.id}/panels/${panelId}`);
      await loadAll();
    } catch { alert('Failed to delete panel.'); }
  };

  // ── Submissions ───────────────────────────────────────────────────────────

  const handleApprove = async () => {
    setActioning(approveModal);
    const id = approveModal;
    setApproveModal(null);
    try {
      await api.post(`/api/applications/${guild.id}/${id}/approve`, { admin_note: approveNote, add_to_staff: addToStaff, rank_id: approveRank || null });
      setApproveNote(''); setAddToStaff(false); setApproveRank('');
      await loadAll();
    } catch { alert('Failed to approve application.'); }
    finally { setActioning(null); }
  };

  const handleDeny = async () => {
    setActioning(denyModal);
    const id = denyModal;
    setDenyModal(null);
    try {
      await api.post(`/api/applications/${guild.id}/${id}/deny`, { admin_note: denyNote });
      setDenyNote('');
      await loadAll();
    } catch { alert('Failed to deny application.'); }
    finally { setActioning(null); }
  };

  const filteredSubs = submissions.filter(s =>
    (subFilter === 'all' || s.status === subFilter) &&
    (subFormFilter === 'all' || s.form_id === parseInt(subFormFilter))
  );

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}><div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading applications...</div>;

  return (
    <div>
      {/* Approve Modal */}
      {approveModal && (
        <div onClick={() => setApproveModal(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '28px', maxWidth: '440px', width: '90%', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '16px' }}>✅ Approve Application</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', background: 'var(--bg-secondary)', borderRadius: '10px', marginBottom: '16px', cursor: 'pointer' }} onClick={() => setAddToStaff(!addToStaff)}>
              <input type="checkbox" checked={addToStaff} onChange={e => setAddToStaff(e.target.checked)} style={{ width: '16px', height: '16px' }} />
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Add to staff on approval</span>
            </div>
            {addToStaff && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Starting Rank (optional)</label>
                <select value={approveRank} onChange={e => setApproveRank(e.target.value)} style={inputStyle}>
                  <option value="">No rank</option>
                  {ranks.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Note to applicant (optional)</label>
              <input type="text" value={approveNote} onChange={e => setApproveNote(e.target.value)} placeholder="Welcome to the team!" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setApproveModal(null)} style={{ padding: '10px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-secondary)' }}>Cancel</button>
              <button onClick={handleApprove} style={{ padding: '10px 24px', background: 'var(--success)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: '14px', color: '#fff', fontWeight: 700 }}>Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* Deny Modal */}
      {denyModal && (
        <div onClick={() => setDenyModal(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '28px', maxWidth: '420px', width: '90%', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '16px' }}>❌ Deny Application</h3>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Reason (optional)</label>
            <input type="text" value={denyNote} onChange={e => setDenyNote(e.target.value)} placeholder="Reason for denial..." style={{ ...inputStyle, marginBottom: '20px' }} />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDenyModal(null)} style={{ padding: '10px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-secondary)' }}>Cancel</button>
              <button onClick={handleDeny} style={{ padding: '10px 24px', background: 'var(--danger)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: '14px', color: '#fff', fontWeight: 700 }}>Deny</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Applications</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
          {forms.length} form{forms.length !== 1 ? 's' : ''} · {panels.length} panel{panels.length !== 1 ? 's' : ''} · {pendingCount > 0 ? `${pendingCount} pending` : 'no pending submissions'}
        </p>
      </div>

      {/* Main tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
        {[
          { key: 'submissions', label: `📋 Submissions${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { key: 'forms', label: `❓ Forms (${forms.length})` },
          { key: 'panels', label: `📤 Panels (${panels.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', border: 'none',
            fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600,
            background: tab === t.key ? 'var(--bg-card)' : 'transparent',
            color: tab === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: tab === t.key ? 'var(--shadow-card)' : 'none',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── SUBMISSIONS TAB ── */}
      {tab === 'submissions' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '4px' }}>
              {['pending', 'approved', 'denied', 'all'].map(s => (
                <button key={s} onClick={() => setSubFilter(s)} style={{
                  padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', border: 'none',
                  fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600,
                  background: subFilter === s ? 'var(--bg-card)' : 'transparent',
                  color: subFilter === s ? 'var(--text-primary)' : 'var(--text-muted)',
                  boxShadow: subFilter === s ? 'var(--shadow-card)' : 'none',
                  textTransform: 'capitalize',
                }}>{s}</button>
              ))}
            </div>
            <select value={subFormFilter} onChange={e => setSubFormFilter(e.target.value)} style={{ padding: '8px 12px', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '13px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}>
              <option value="all">All Forms</option>
              {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>

          {filteredSubs.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', padding: '60px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
              <div style={{ color: 'var(--text-muted)' }}>No {subFilter === 'all' ? '' : subFilter} submissions.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredSubs.map(app => (
                <div key={app.id} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'var(--gradient-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 700, color: '#fff' }}>
                        {(app.applicant_display_name || app.applicant_username || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{app.applicant_display_name || app.applicant_username || 'Unknown'}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {app.form_name && <span style={{ background: 'var(--bg-secondary)', padding: '1px 7px', borderRadius: '100px', marginRight: '6px' }}>{app.form_name}</span>}
                          {new Date(app.submitted_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>

                  <button onClick={() => setExpanded(expanded === app.id ? null : app.id)}
                    style={{ width: '100%', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 14px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'left', marginBottom: app.status === 'pending' ? '12px' : '0' }}>
                    {expanded === app.id ? '▲ Hide' : '▼ View'} answers ({app.answers?.length || 0})
                  </button>

                  {expanded === app.id && app.answers?.length > 0 && (
                    <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {app.answers.map((ans, i) => (
                        <div key={i} style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '10px 14px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '4px' }}>{ans.question_text}</div>
                          <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 }}>{ans.answer_text || <em style={{ color: 'var(--text-muted)' }}>Skipped</em>}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {app.admin_note && (
                    <div style={{ background: 'rgba(52,152,219,0.08)', border: '1px solid rgba(52,152,219,0.2)', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--info)', marginBottom: '3px' }}>Admin Note</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{app.admin_note}</div>
                    </div>
                  )}

                  {app.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => setApproveModal(app.id)} disabled={actioning === app.id}
                        style={{ padding: '10px 20px', background: 'var(--success)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'var(--font-display)' }}>✅ Approve</button>
                      <button onClick={() => setDenyModal(app.id)} disabled={actioning === app.id}
                        style={{ padding: '10px 20px', background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--font-body)' }}>❌ Deny</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── FORMS TAB ── */}
      {tab === 'forms' && (
        <div style={{ display: 'grid', gridTemplateColumns: selectedForm ? '280px 1fr' : '1fr', gap: '24px' }}>
          {/* Form list */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>Your Forms</div>
              <button onClick={() => setCreatingForm(!creatingForm)} style={{ padding: '8px 14px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>+ New Form</button>
            </div>

            {editingForm && (
              <div style={{ ...cardStyle, marginBottom: '12px', border: '2px solid var(--accent-primary)' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>Edit Form</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input type="text" value={editingForm.name} onChange={e => setEditingForm(p => ({...p, name: e.target.value}))} placeholder="Form name" style={inputStyle} />
                  <textarea value={editingForm.description || ''} onChange={e => setEditingForm(p => ({...p, description: e.target.value}))} placeholder="Description (optional)" rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Idle timeout (minutes)</label>
                    <input type="number" min="1" max="60" value={editingForm.timeout_minutes} onChange={e => setEditingForm(p => ({...p, timeout_minutes: e.target.value}))} style={inputStyle} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handleUpdateForm} style={{ flex: 1, padding: '10px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>Save Changes</button>
                    <button onClick={() => setEditingForm(null)} style={{ padding: '10px 16px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {creatingForm && (
              <div style={{ ...cardStyle, marginBottom: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input type="text" value={newFormName} onChange={e => setNewFormName(e.target.value)} placeholder="Form name e.g. Staff Application" style={inputStyle} />
                  <textarea value={newFormDesc} onChange={e => setNewFormDesc(e.target.value)} placeholder="Description (optional)" rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Idle timeout (minutes)</label>
                    <input type="number" min="5" max="60" value={newFormTimeout} onChange={e => setNewFormTimeout(e.target.value)} style={inputStyle} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handleCreateForm} style={{ flex: 1, padding: '10px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>Create</button>
                    <button onClick={() => setCreatingForm(false)} style={{ padding: '10px 16px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {forms.length === 0 && !creatingForm ? (
              <div style={{ ...cardStyle, textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>❓</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No forms yet. Create one to get started.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {forms.map(f => (
                  <div key={f.id} onClick={() => setSelectedForm(selectedForm?.id === f.id ? null : f)}
                    style={{ ...cardStyle, cursor: 'pointer', border: `1px solid ${selectedForm?.id === f.id ? 'var(--accent-primary)' : 'var(--border)'}`, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{f.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {f.submission_count || 0} submissions · ⏰ {f.timeout_minutes}min
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => handleToggleFormStatus(f)} style={{
                          padding: '4px 10px', borderRadius: '100px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700,
                          background: f.status === 'open' ? 'rgba(46,204,113,0.15)' : 'rgba(231,76,60,0.15)',
                          color: f.status === 'open' ? 'var(--success)' : 'var(--danger)',
                        }}>{f.status === 'open' ? '● Open' : '● Closed'}</button>
                        <button onClick={() => { setEditingForm({...f}); setSelectedForm(null); }} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                        <button onClick={() => handleDeleteForm(f.id)} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--danger)', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', fontSize: '12px' }}>🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Question editor */}
          {selectedForm && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>{selectedForm.name}</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{formQuestions.length} question{formQuestions.length !== 1 ? 's' : ''}</div>
                </div>
                <button onClick={() => { setCreatingQuestion(true); setEditingQuestion(null); }}
                  style={{ padding: '10px 18px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>+ Add Question</button>
              </div>

              {(creatingQuestion || editingQuestion) && (
                <div style={{ ...cardStyle, marginBottom: '16px' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                    {editingQuestion ? 'Edit Question' : 'New Question'}
                  </div>
                  <QuestionForm
                    initial={editingQuestion}
                    onSave={handleSaveQuestion}
                    onCancel={() => { setCreatingQuestion(false); setEditingQuestion(null); }}
                  />
                </div>
              )}

              {formQuestions.length === 0 ? (
                <div style={{ ...cardStyle, textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>❓</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No questions yet. Add the first question above.</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {formQuestions.map((q, i) => (
                    <div key={q.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: 'var(--gradient-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>{i + 1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{q.question_text}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {q.question_type === 'multiple_choice' ? '🔘 Multiple Choice' : '📝 Text'}
                          {q.required ? ' · Required' : ' · Optional'}
                          {q.min_length ? ` · Min ${q.min_length}` : ''}
                          {q.max_length ? ` · Max ${q.max_length}` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button onClick={() => { setEditingQuestion(q); setCreatingQuestion(false); }} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>✏️</button>
                        <button onClick={() => handleDeleteQuestion(q.id)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--danger)', background: 'transparent', fontSize: '12px', color: 'var(--danger)', cursor: 'pointer' }}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── PANELS TAB ── */}
      {tab === 'panels' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Panels are Discord messages with buttons for each form.</div>
            <button onClick={() => setCreatingPanel(!creatingPanel)} style={{ padding: '10px 20px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>+ New Panel</button>
          </div>

          {creatingPanel && (
            <div style={{ ...cardStyle, marginBottom: '20px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--text-primary)', marginBottom: '16px' }}>New Panel</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Panel Name</label>
                  <input type="text" value={newPanelName} onChange={e => setNewPanelName(e.target.value)} placeholder="e.g. M.A.S.H Staff Applications" style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Description (optional)</label>
                  <textarea value={newPanelDesc} onChange={e => setNewPanelDesc(e.target.value)} placeholder="Instructions for applicants..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Forms to include</label>
                  {forms.length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No forms yet — create a form first.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {forms.map(f => (
                        <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: '8px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={newPanelForms.includes(f.id)}
                            onChange={e => setNewPanelForms(prev => e.target.checked ? [...prev, f.id] : prev.filter(id => id !== f.id))} />
                          <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{f.name}</span>
                          <span style={{ fontSize: '11px', color: f.status === 'open' ? 'var(--success)' : 'var(--danger)', marginLeft: 'auto' }}>● {f.status}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={handleCreatePanel} style={{ flex: 1, padding: '12px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>Create Panel</button>
                  <button onClick={() => setCreatingPanel(false)} style={{ padding: '12px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', color: 'var(--text-secondary)' }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {panels.length === 0 && !creatingPanel ? (
            <div style={{ ...cardStyle, textAlign: 'center', padding: '60px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📤</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>No panels yet</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Create a panel to post application buttons in your Discord server.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {panels.map(panel => (
                <div key={panel.id} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-primary)' }}>{panel.name}</div>
                      {panel.description && <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>{panel.description}</div>}
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                        {panel.forms?.length || 0} form{(panel.forms?.length || 0) !== 1 ? 's' : ''}
                        {panel.message_id && ' · Posted to Discord ✅'}
                      </div>
                    </div>
                    <button onClick={() => { setEditingPanel(panel); setEditPanelForms((panel.forms || []).map(f => f.id)); }} style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}>✏️ Edit Forms</button>
                    <button onClick={() => handleDeletePanel(panel.id)} style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--danger)', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                  </div>

                  {panel.forms?.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                      {panel.forms.map(f => (
                        <span key={f.id} style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '100px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                          {f.name} <span style={{ color: f.status === 'open' ? 'var(--success)' : 'var(--danger)' }}>● {f.status}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {editingPanel?.id === panel.id && (
                    <div style={{ background: 'var(--bg-secondary)', borderRadius: '10px', padding: '14px', marginBottom: '12px', border: '1px solid var(--accent-primary)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' }}>Edit Panel Forms</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                        {forms.map(f => (
                          <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'var(--bg-card)', borderRadius: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={editPanelForms.includes(f.id)}
                              onChange={e => setEditPanelForms(prev => e.target.checked ? [...prev, f.id] : prev.filter(id => id !== f.id))} />
                            <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{f.name}</span>
                            <span style={{ fontSize: '11px', color: f.status === 'open' ? 'var(--success)' : 'var(--danger)', marginLeft: 'auto' }}>● {f.status}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleUpdatePanel} style={{ flex: 1, padding: '9px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>Save</button>
                        <button onClick={() => { setEditingPanel(null); setEditPanelForms([]); }} style={{ padding: '9px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                        {panel.channel_id ? 'Update channel (or leave blank to reuse existing)' : 'Select channel to post to'}
                      </label>
                      <ChannelSelect
                        guildId={guild.id}
                        value={selectedPanel === panel.id ? pushChannel : (panel.channel_id || '')}
                        onChange={val => { setSelectedPanel(panel.id); setPushChannel(val); }}
                        placeholder={panel.channel_id ? 'Same channel as before' : 'Select channel...'}
                      />
                    </div>
                    <button
                      onClick={() => handlePushPanel(panel.id)}
                      disabled={pushingPanel === panel.id}
                      style={{
                        padding: '10px 20px', flexShrink: 0,
                        background: pushingPanel === panel.id ? 'var(--text-muted)' : 'var(--gradient-accent)',
                        color: '#fff', border: 'none', borderRadius: '10px',
                        fontSize: '13px', fontWeight: 700, cursor: pushingPanel === panel.id ? 'not-allowed' : 'pointer',
                        fontFamily: 'var(--font-display)',
                      }}>
                      {pushingPanel === panel.id ? 'Pushing...' : panel.message_id ? '🔄 Update Panel' : '📤 Push to Discord'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
